import type { FocusEvent, FormEvent } from "react";
import { Typography } from "@mui/material";
import FormItem from "@components/FormItem/FormItem";
import { samePortValidation } from "@features/vessel-call/vessel-call-status/PortServices/form/utils";
import type { PortServicesFormData } from "@features/vessel-call/vessel-call-status/PortServices/schema/portServicesFormSchema";
import { getBunkerBargeCandidatesByIMO } from "@services/vesselCallApi";
import { runFieldNavigation } from "@telemetry/runFieldNavigation";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

const IMO_DOES_NOT_EXIST_MESSAGE_KEY = "form-validation.imo-does-not-exist";
const VALID_BUNKER_BARGE_IMO_REQUIRED_MESSAGE_KEY =
  "form-validation.valid-bunker-barge-imo-required";
const CANNOT_BE_SAME_IMO_MESSAGE_KEY = "form-validation.cannot-be-same-imo";
const BUNKER_BARGE_IMO_MAX_DIGITS = 8;
const BUNKER_BARGE_LOOKUP_FIELD_NAVIGATION_CONTEXT = {
  module: "vessel_call",
  screen: "Port Service Bunker Barge Details",
  action: "lookup_bunker_barge_candidates",
  fromField: "bunkerBargeIMO",
  toField: "bunkerBargeName",
  entityType: "vessel",
};

type BunkerBargeIMOFieldProps = {
  formData: UseFormReturn<PortServicesFormData>;
  updateFormData: (key: string, value: unknown) => void;
  langBase: string;
  currentVesselImoNumber?: string;
  selectedPortCode?: string;
  invalidMessageKey?: string;
  testId?: string;
};

const BunkerBargeIMOField = ({
  formData,
  updateFormData,
  langBase,
  currentVesselImoNumber,
  selectedPortCode,
  invalidMessageKey = "form-validation.imo-does-not-exist",
  testId = "bunkerBargeIMO",
}: BunkerBargeIMOFieldProps) => {
  const { t } = useTranslation();
  const bunkerBargeIMO = formData.watch("bunkerBargeIMO");
  const bunkerBargeName = formData.watch("bunkerBargeName");
  const isVesselFound = Boolean(bunkerBargeIMO && bunkerBargeName);

  const setBunkerBargeLookupPending = (value: boolean) => {
    formData.setValue("bunkerBargeLookupPending", value);
    updateFormData("bunkerBargeLookupPending", value);
  };

  const setBunkerBargeLookupErrorMessageKey = (messageKey = "") => {
    formData.setValue("bunkerBargeLookupErrorMessageKey", messageKey);
    updateFormData("bunkerBargeLookupErrorMessageKey", messageKey);
  };

  const setBunkerBargeCandidate = (name = "", vesselCallId = "") => {
    formData.setValue("bunkerBargeName", name);
    formData.setValue("bunkerBargeVesselCallId", vesselCallId);
    updateFormData("bunkerBargeName", name);
    updateFormData("bunkerBargeVesselCallId", vesselCallId);

    if (name) {
      setBunkerBargeLookupErrorMessageKey();
      formData.clearErrors(["bunkerBargeName", "bunkerBargeIMO"]);
    }
  };

  const setBunkerBargeIMOError = (messageKey: string) => {
    setBunkerBargeLookupErrorMessageKey(messageKey);
    formData.setError("bunkerBargeIMO", {
      type: "manual",
      message: t(messageKey),
    });
  };

  const handleBunkerBargeIMOBlur = async (event: FocusEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value.trim();
    formData.setValue("bunkerBargeIMO", value);
    updateFormData("bunkerBargeIMO", value);

    if (!value || value.length > 8) {
      setBunkerBargeLookupPending(false);
      setBunkerBargeLookupErrorMessageKey();
      setBunkerBargeCandidate();
      await formData.trigger("bunkerBargeIMO");
      return;
    }

    if (value === currentVesselImoNumber?.toString().trim()) {
      setBunkerBargeLookupPending(false);
      setBunkerBargeLookupErrorMessageKey();
      setBunkerBargeCandidate();
      setBunkerBargeIMOError(CANNOT_BE_SAME_IMO_MESSAGE_KEY);
      return;
    }

    setBunkerBargeLookupPending(true);
    setBunkerBargeLookupErrorMessageKey();
    formData.clearErrors("bunkerBargeIMO");

    const result = await runFieldNavigation(BUNKER_BARGE_LOOKUP_FIELD_NAVIGATION_CONTEXT, () =>
      getBunkerBargeCandidatesByIMO({
        service: "bff-vessel-call",
        params: {
          imo: value,
          bunkeringOnly: false,
        },
      }),
    ).catch(() => undefined);

    const currentBunkerBargeIMO = formData.getValues("bunkerBargeIMO")?.toString().trim();
    if (currentBunkerBargeIMO !== value) {
      return;
    }

    setBunkerBargeLookupPending(false);
    const bunkerBarge = result?.items[0];

    if (!bunkerBarge) {
      setBunkerBargeCandidate();
      setBunkerBargeIMOError(IMO_DOES_NOT_EXIST_MESSAGE_KEY);
      return;
    }

    if (!samePortValidation(bunkerBarge.port, selectedPortCode)) {
      setBunkerBargeCandidate();
      setBunkerBargeIMOError(IMO_DOES_NOT_EXIST_MESSAGE_KEY);
      return;
    }

    const resolvedBunkerBargeName = bunkerBarge?.bunkerBargeName?.trim();

    if (bunkerBarge.canOperateAsBunkerBarge === false) {
      setBunkerBargeCandidate();
      setBunkerBargeIMOError(VALID_BUNKER_BARGE_IMO_REQUIRED_MESSAGE_KEY);
      return;
    }

    if (!resolvedBunkerBargeName) {
      setBunkerBargeCandidate();
      setBunkerBargeIMOError(invalidMessageKey);
      return;
    }

    setBunkerBargeCandidate(resolvedBunkerBargeName, bunkerBarge?.vesselCallId ?? "");
  };

  const handleBunkerBargeIMOInput = (event: FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value.trim();

    setBunkerBargeLookupPending(Boolean(value && value.length <= 8));
    setBunkerBargeLookupErrorMessageKey();
    setBunkerBargeCandidate();
    formData.clearErrors(["bunkerBargeName", "bunkerBargeIMO"]);
  };

  return (
    <>
      <div className="col-span-2 col-start-1">
        <FormItem<PortServicesFormData, "bunkerBargeIMO">
          name="bunkerBargeIMO"
          type="number"
          maxDigits={BUNKER_BARGE_IMO_MAX_DIGITS}
          label={t(`${langBase}.fields.bunker-barge-imo`)}
          testId={testId}
          onFieldChange={updateFormData}
          onFocus={() => formData.clearErrors(["bunkerBargeName", "bunkerBargeIMO"])}
          slotProps={{
            htmlInput: {
              min: "1",
              onInput: handleBunkerBargeIMOInput,
              onBlur: handleBunkerBargeIMOBlur,
            },
          }}
        />
      </div>
      <div className="col-span-2 mb-4">
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {t(`${langBase}.fields.bunker-barge-name`)}
        </Typography>
        {isVesselFound && (
          <Typography variant="body1" sx={{ fontWeight: 700, fontSize: 16 }}>
            {bunkerBargeName}
          </Typography>
        )}
      </div>
    </>
  );
};

export default BunkerBargeIMOField;
